/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Navigation Hook with Progress Indicator
 *
 * Wraps Next.js router. The progress bar is driven by history.pushState
 * interception and pathname changes — no manual event dispatch needed
 * for push/replace. Back/forward are handled by popstate.
 */

"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";

export function useNavigationWithProgress() {
  const router = useRouter();

  const push = useCallback(
    (href: string, options?: { scroll?: boolean }) => {
      router.push(href, options);
    },
    [router],
  );

  const replace = useCallback(
    (href: string, options?: { scroll?: boolean }) => {
      router.replace(href, options);
    },
    [router],
  );

  const back = useCallback(() => {
    router.back();
  }, [router]);

  const forward = useCallback(() => {
    router.forward();
  }, [router]);

  const refresh = useCallback(() => {
    router.refresh();
  }, [router]);

  const prefetch = useCallback(
    (href: string) => {
      router.prefetch(href);
    },
    [router],
  );

  return {
    push,
    replace,
    back,
    forward,
    refresh,
    prefetch,
    router,
  };
}
